import React, { useEffect, useState } from "react";
import ReplicaTour from "./ReplicaTour";
import { MASTER_ENABLER_STEPS, MasterEnablerPane, MASTER_ENABLER_SECTION, DeleteConfirmDialog } from "./masterEnabler";
import { WATCH_PHOTOS } from "./watchScripts";
import { LayoutGrid, GitBranch, Calendar, BookOpen, FileSpreadsheet, Printer, Plus, Share2,
  Lightbulb, Merge, Tags, Trash2, Search, CheckSquare, Users, FolderPlus, MoveRight, List,
  Pencil, Cpu, Undo2, UsersRound, Server, Upload, HardDriveDownload, Download, Link2, RefreshCw,
  Network, ListOrdered, ArrowDownAZ, ArrowDownWideNarrow, Coins, Bell, Cake, Heart, Flower2, Star, X, Paperclip } from "lucide-react";

// Positional map: manual step k -> the replica control the invisible hand lands on.
// MUST stay the same length & order as the backend manual steps for `family_tree` (26).
const TARGET = [
  "rep-new-tree",       // 0  Create a tree
  "rep-add",            // 1  Add a person / record
  "rep-card-edit",      // 2  Edit or delete a record
  "rep-link",           // 3  Link relationships (parents, spouses, siblings)
  "rep-spouse-link",    // 4  Recommended way to link a spouse (dropdown vs typing)
  "rep-autolink",       // 5  Auto-link relationships from typed names
  "rep-autoarrange",    // 6  Auto-arrange the diagram
  "rep-viewswitch",     // 7  Switch views and highlight a line
  "rep-modeswitch",     // 8  Switch modes (Generic / Family)
  "rep-datemode",       // 9  Full date vs Year only (per field)
  "rep-idea",           // 10 Ranking override — Master & Submaster (IDEA)
  "rep-search",         // 11 Search and show/hide dates
  "rep-selectall",      // 12 Select people and use bulk actions
  "rep-groups",         // 13 Manage saved groups
  "rep-share",          // 14 Share a read-only link
  "rep-suggestions",    // 15 Review viewer suggestions
  "rep-merge",          // 16 Merge or delete a tree
  "rep-labels",         // 17 Rename field labels
  "rep-print",          // 18 Print a diagram or poster
  "rep-csv",            // 19 Export a spreadsheet or open the manual
  "rep-automation",     // 20 FopFo Automation, Clusters & Security profile
  "rep-cluster-export", // 21 Cluster cache — export items
  "rep-cluster-import", // 22 Cluster cache — import items
  "rep-recent",         // 23 Undo a move (Recently moved)
  "rep-download",       // 24 Download all data (App Store / Play purchase)
  "rep-delete-confirm", // 25 Delete all customer-loaded data (Master Enabler)
];

// Which "scene" the replica screen shows for a given active control.
const SCENE = {
  "rep-link": "edit", "rep-spouse-link": "edit",
  "rep-autoarrange": "diagram",
  "rep-modeswitch": "generic", "rep-datemode": "generic", "rep-idea": "generic",
};
const sceneFor = (rid) => SCENE[rid] || "family";

const Btn = ({ id, icon: Icon, label, blue }) => (
  <div data-rid={id} className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] ${blue ? "border-transparent bg-[var(--primary)] text-white" : "border-black/15 dark:border-white/15"}`}>
    <Icon size={13} /> {label}
  </div>
);

// ---------------- GENERIC org-chart scene (faithful compact RankBoard Department/Unit view) ---------
const ORG_TIERS = [
  [
    { name: "Executive Command & Strategy Office", rank: "A", color: "#0F62FE",
      members: [{ n: "Maximilian Featherstone", t: "Master & Commander", g: 2 }, { n: "Test One", t: "Chief of Staff", g: 3 }] },
  ],
  [
    { name: "Engineering & Propulsion", rank: "B", color: "#0043CE",
      members: [{ n: "Christopher Beaumont", t: "Chief Engineer", g: 8 }, { n: "Anastasia Silva", t: "Officer", g: 12 }, { n: "Fitzgerald Silva", t: "Officer", g: 14 }] },
    { name: "Ship's Navigation", rank: "B", color: "#FF832B",
      members: [{ n: "Alexander Feathe.", t: "Navigator", g: 11 }, { n: "Maximilian Silva", t: "Officer", g: 15 }] },
    { name: "Culinary & Provisioning", rank: "B", color: "#8A3FFC",
      members: [{ n: "Wilhelmina Aterni", t: "Head Chef", g: 13 }, { n: "Genevieve Vande.", t: "Officer", g: 18 }] },
  ],
];
const ORG_LEGEND = [
  { Icon: Cake, label: "Birthday", color: "#0F62FE" }, { Icon: Heart, label: "Wedding", color: "#24A148" }, { Icon: Flower2, label: "Memorial", color: "#DA1E28" },
];

function GenericOrgScene({ ideaActive, P = (t) => t }) {
  return (
    <div className="p-3" data-testid="ft-generic-scene">
      <div className="mb-2 flex flex-wrap items-center gap-1.5 rounded-md border border-black/10 bg-black/[0.02] px-2 py-1.5 dark:border-white/10 dark:bg-white/[0.03]">
        <span className="inline-flex items-center gap-1 rounded bg-[var(--primary)] px-2 py-1 text-[10px] text-white"><Network size={11} /> {P("Department/Unit View")}</span>
        <span className="inline-flex items-center gap-1 rounded border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><ListOrdered size={11} /> {P("Unit Rank View")}</span>
        <span className="ms-1 inline-flex items-center gap-1 rounded border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><ArrowDownAZ size={11} /> A→Z</span>
        <span className="inline-flex items-center gap-1 rounded border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><Coins size={11} /> {P("People by Salary Grade")}</span>
        <span className="inline-flex items-center gap-1 rounded border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><ArrowDownWideNarrow size={11} /> High→Low</span>
      </div>
      {/* IDEA ranking-override chips (Master / Submaster) */}
      <div data-rid="rep-idea" className={`mb-2 flex flex-wrap items-center gap-1.5 rounded-md border px-2 py-1.5 text-[10px] transition ${ideaActive ? "border-[var(--primary)] bg-[var(--primary)]/[0.06]" : "border-black/10 bg-white/40 dark:border-white/10 dark:bg-white/[0.02]"}`}>
        <span className="font-semibold opacity-70">{P("IDEA override:")}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-[var(--primary)]"><Star size={9} className="fill-current" /> {P("Master")}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-[var(--primary)]">{P("Submaster")}</span>
        <span className="opacity-55">{P("First Designation · Second Designation · Exigency")}</span>
      </div>
      <div className="max-h-[280px] overflow-auto rounded-md border border-black/10 bg-white/40 p-3 dark:border-white/10 dark:bg-white/[0.02]" data-testid="ft-org-chart">
        <div className="flex flex-col items-center gap-4">
          {ORG_TIERS.map((tier, ti) => (
            <div key={ti} className="flex flex-wrap justify-center gap-3">
              {tier.map((u) => (
                <div key={u.name} className="overflow-hidden rounded-lg border border-black/10 dark:border-white/12">
                  <div className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-bold text-white" style={{ background: u.color }}>
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-white/25 text-[9px]">{u.rank}</span>
                    <span className="truncate">{u.name}</span><span className="ms-auto text-[9px] font-medium opacity-90">{u.members.length}</span>
                  </div>
                  <div className="flex flex-wrap justify-center gap-1.5 p-2">
                    {u.members.map((m, k) => (
                      <div key={k} className="w-[112px] rounded-md border border-black/12 bg-white px-2 py-1.5 shadow-sm dark:border-white/15 dark:bg-neutral-800" style={{ borderTop: `3px solid ${u.color}` }}>
                        <div className="truncate text-[11px] font-bold leading-tight">{m.n}</div>
                        <div className="truncate text-[10px] opacity-70">{m.t}</div>
                        <div className="truncate text-[10px] opacity-55">{P("Grade")} {m.g}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-2 rounded-md border border-black/10 bg-white/40 p-2 dark:border-white/10 dark:bg-white/[0.02]" data-testid="ft-nearest-mini">
        <div className="mb-1 flex items-center gap-1.5">
          <Bell size={13} className="text-[var(--primary)]" /><span className="font-head text-[12px] font-semibold">{P("NEAREST")}</span>
          <span className="rounded-full bg-[#DA1E28]/15 px-1.5 text-[9px] font-semibold text-[#DA1E28]">2</span>
          <div className="ms-auto flex items-center gap-2">{ORG_LEGEND.map((l) => <span key={l.label} className="flex items-center gap-0.5 text-[9px] opacity-70"><l.Icon size={9} style={{ color: l.color }} /> {P(l.label)}</span>)}</div>
        </div>
        <div className="flex items-center gap-2 rounded border border-[#0F62FE] p-1.5 text-[10px]">
          <Cake size={12} className="text-[#0F62FE]" /><span className="flex-1 truncate"><b>Maximilian Featherstone</b> · 50th birthday</span>
          <span className="rounded bg-black/5 px-1.5 py-0.5 dark:bg-white/10">12d</span>
        </div>
      </div>
    </div>
  );
}

// ---------------- DIAGRAM scene (family diagram + Auto-arrange) ----------------
function DiagramScene({ P = (t) => t }) {
  return (
    <div className="p-3" data-testid="ft-diagram-scene">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
        <span className="opacity-60">{P("Orientation:")}</span>
        <span className="inline-flex items-center gap-1 rounded bg-[var(--primary)] px-2 py-1 text-white">{P("Portrait")}</span>
        <span className="inline-flex items-center gap-1 rounded border border-black/15 px-2 py-1 dark:border-white/15">{P("Landscape")}</span>
        <div data-rid="rep-autoarrange" className="ms-auto inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 dark:border-white/15"><RefreshCw size={12} /> {P("Auto-arrange")}</div>
      </div>
      <div className="relative rounded-md border border-black/10 bg-white/40 p-4 dark:border-white/10 dark:bg-white/[0.02]" style={{ minHeight: 220 }}>
        <div className="mx-auto w-40 rounded-md border-2 border-[#0F62FE] bg-white px-2 py-1 text-center text-[11px] font-semibold shadow dark:bg-neutral-800">Musa Bello</div>
        <div className="mx-auto h-4 w-px bg-slate-400" />
        <div className="flex justify-center gap-6">
          {["Amina Bello", "Tunde Bello"].map((n) => (
            <div key={n} className="w-32 rounded-md border-2 border-[#24A148] bg-white px-2 py-1 text-center text-[11px] font-semibold shadow dark:bg-neutral-800">{n}</div>
          ))}
        </div>
        <div className="mt-2 flex justify-center"><div className="w-28 rounded-md border-2 border-[#8A3FFC] bg-white px-2 py-1 text-center text-[11px] font-semibold shadow dark:bg-neutral-800">Zara Bello</div></div>
      </div>
      <p className="mt-2 text-[10px] opacity-50">{P("Drag nodes to reposition · connect a node's dot to another to set the parent.")}</p>
    </div>
  );
}

// ---------------- EDIT scene (person form with relationship + spouse dropdowns) ----------------
function EditScene({ P = (t) => t }) {
  return (
    <div className="absolute inset-0 z-10 flex items-start justify-center rounded-xl bg-black/40 p-3 backdrop-blur-[1px]" data-testid="ft-edit-scene">
      <div className="mt-6 w-full max-w-[300px] rounded-lg border border-black/10 bg-white p-3 text-black shadow-2xl dark:border-white/15 dark:bg-neutral-900 dark:text-white">
        <div className="mb-2 font-head text-sm font-semibold">{P("Edit record")} — Tunde Bello</div>
        <div className="space-y-2 text-[11px]">
          <div><div className="mb-0.5 opacity-60">{P("Relationships")}</div>
            <div data-rid="rep-link" className="space-y-1 rounded-md border border-[var(--primary)]/30 bg-[var(--primary)]/[0.04] p-2">
              <div className="flex items-center justify-between rounded border border-black/15 px-2 py-1 dark:border-white/15"><span className="opacity-60">{P("Mother")}</span><span>Amina Bello ▾</span></div>
              <div className="flex items-center justify-between rounded border border-black/15 px-2 py-1 dark:border-white/15"><span className="opacity-60">{P("Father")}</span><span>Musa Bello ▾</span></div>
              <div data-rid="rep-spouse-link" className="flex items-center justify-between rounded border border-[var(--primary)] px-2 py-1"><span className="opacity-60">{P("Spouse")}</span><span className="inline-flex items-center gap-1"><Link2 size={11} className="text-[var(--primary)]" /> {P("Select from list")} ▾</span></div>
              <div className="text-[9px] italic opacity-55">{P("Tip: choose from the dropdown to link an existing person rather than typing a new name.")}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FamiligiPane({ me, activeRid, P = (t) => t }) {
  const [mode, setMode] = useState("family");
  const scene = sceneFor(activeRid);
  useEffect(() => { if (scene === "generic") setMode("generic"); else if (scene === "family" || scene === "diagram" || scene === "edit") setMode("family"); }, [scene]);

  const members = [
    { n: "Musa Bello (72)", r: "Senior", p: 9 }, { n: "Amina Bello (48)", r: "Adult", p: 2 },
    { n: "Tunde Bello (26)", r: "Adult", p: 5 }, { n: "Zara Bello (9)", r: "Child", p: 8 },
  ];
  const generic = mode === "generic";
  return (
    <div className="relative rounded-xl border border-black/10 bg-white text-black shadow-lg dark:border-white/10 dark:bg-neutral-900 dark:text-white">
      <div className={`origin-top transition-all duration-700 ${me ? "scale-[0.97] opacity-40 blur-[1.5px]" : "opacity-100"}`}>
      {/* top bar */}
      <div className="flex items-center justify-between border-b border-black/10 px-3 py-2 dark:border-white/10">
        <span className="font-head text-sm font-semibold">{generic ? "StressOrg · Generic" : P("Family Tree")}</span>
        <div className="flex flex-wrap items-center gap-1">
          <button type="button" onClick={() => setMode((m) => m === "generic" ? "family" : "generic")} data-rid="rep-modeswitch" title="Toggle Generic / Family mode"
            className="inline-flex overflow-hidden rounded-md border border-black/15 text-[11px] dark:border-white/15">
            <span className={`flex items-center gap-1 px-2 py-1 ${generic ? "bg-[var(--primary)] text-white" : ""}`}><UsersRound size={12} /> {P("Generic")}</span>
            <span className={`flex items-center gap-1 px-2 py-1 ${!generic ? "bg-[var(--primary)] text-white" : ""}`}><UsersRound size={12} /> {P("Family")}</span>
          </button>
          <div data-rid="rep-viewswitch" className="inline-flex overflow-hidden rounded-md border border-black/15 text-[11px] dark:border-white/15">
            <span className="flex items-center gap-1 bg-[var(--primary)] px-2 py-1 text-white"><LayoutGrid size={12} /> {generic ? P("Chart") : P("Cards")}</span>
            <span className="flex items-center gap-1 px-2 py-1"><GitBranch size={12} /> {P("Diagram")}</span>
          </div>
          <div data-rid="rep-datemode" className="inline-flex overflow-hidden rounded-md border border-black/15 text-[10px] dark:border-white/15">
            <span className="flex items-center gap-1 bg-[var(--primary)] px-1.5 py-1 text-white"><Calendar size={11} /> {P("Full")}</span>
            <span className="px-1.5 py-1">{P("Year")}</span>
          </div>
          <Btn id="rep-dob" icon={Calendar} label={P("DOB")} />
          <Btn id="rep-dod" icon={Calendar} label={P("DOD")} />
          <Btn id="rep-manual" icon={BookOpen} label={P("Manual")} />
          <Btn id="rep-csv" icon={FileSpreadsheet} label={P("CSV")} />
          <Btn id="rep-export" icon={Download} label={P("Export CSV/JSON")} />
          <Btn id="rep-attach" icon={Paperclip} label={P("Photos & docs")} />
          <Btn id="rep-print" icon={Printer} label={P("Print")} />
          <Btn id="rep-add" icon={Plus} label={P("Add")} blue />
        </div>
      </div>
      {/* automation card */}
      <div data-rid="rep-automation" className="mx-3 mt-2 flex items-center gap-2 rounded-md border border-black/10 bg-black/[0.02] px-3 py-1.5 text-[11px] dark:border-white/10 dark:bg-white/[0.03]">
        <Cpu size={13} className="text-[var(--primary)]" /> FopFo Automation · Clusters · Security profile
        <span className="ms-auto rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-emerald-600">{P("Active")}</span>
      </div>
      {/* tree bar */}
      <div className="mx-3 mt-2 flex flex-wrap items-center gap-1">
        <span className="rounded-md border border-black/15 px-2 py-1 text-[11px] dark:border-white/15">{generic ? "Tree: StressOrg ▾" : "Tree: The Bello Family ▾"}</span>
        <Btn id="rep-new-tree" icon={Plus} label={P("New Tree")} />
        <Btn id="rep-autolink" icon={Link2} label={P("Auto-link")} />
        <Btn id="rep-share" icon={Share2} label={P("Share")} />
        <Btn id="rep-suggestions" icon={Lightbulb} label={P("Suggestions")} />
        <Btn id="rep-merge" icon={Merge} label={P("Merge")} />
        <Btn id="rep-labels" icon={Tags} label={P("Field labels")} />
        {generic && <Btn id="rep-salary-grades" icon={Coins} label={P("Salary grades")} />}
        <div data-rid="rep-deltree" className="inline-flex items-center rounded-md border border-black/15 px-1.5 py-1 dark:border-white/15"><Trash2 size={13} /></div>
      </div>
      {/* search + selection toolbar */}
      <div className="mx-3 mt-2 flex flex-wrap items-center gap-1">
        <div data-rid="rep-search" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[11px] opacity-60 dark:border-white/15"><Search size={12} /> {P("Search…")}</div>
        <Btn id="rep-selectall" icon={CheckSquare} label={P("Select all")} />
        <Btn id="rep-groups" icon={Users} label={P("Groups")} />
        <Btn id="rep-creategroup" icon={FolderPlus} label={P("Create group")} />
        <Btn id="rep-move" icon={MoveRight} label={P("Move")} />
        <Btn id="rep-list" icon={List} label={P("List")} />
        <div data-rid="rep-recent" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[11px] dark:border-white/15"><Undo2 size={12} /> {P("Recently moved")}</div>
      </div>
      {/* content region */}
      {scene === "generic" ? <GenericOrgScene ideaActive={activeRid === "rep-idea"} P={P} />
        : scene === "diagram" ? <DiagramScene P={P} />
        : (
        <div className="grid grid-cols-2 gap-2 p-3">
          {members.map((m, k) => (
            <div key={k} data-rid={k === 0 ? "rep-card-edit" : undefined} className="flex items-center gap-2 rounded-lg border border-black/10 px-2 py-2 dark:border-white/10">
              <img src={WATCH_PHOTOS[m.p]} alt="" className="h-8 w-8 rounded-full object-cover" />
              <div className="min-w-0"><div className="truncate text-xs font-medium">{m.n}</div><div className="text-[10px] opacity-50">{P(m.r)}</div></div>
              <div className="ms-auto flex items-center gap-1 opacity-60"><Pencil size={12} /><Trash2 size={12} /></div>
            </div>
          ))}
        </div>
      )}
      </div>
      {/* Master Enabler mini-panel */}
      <div style={me ? { backgroundColor: "color-mix(in srgb, var(--primary) 8%, transparent)" } : undefined}
        className={`mx-3 mb-3 rounded-md border p-2 transition-all duration-700 ${me ? "scale-[1.03] border-[var(--primary)] shadow-lg" : "border-black/10 bg-black/[0.02] dark:border-white/10 dark:bg-white/[0.03]"}`}>
        <div className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide opacity-55"><Server size={11} className="text-[var(--primary)]" /> {P("Master Enabler")}</div>
        <div className="flex flex-wrap items-center gap-1">
          <div data-rid="rep-cluster-export" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><Upload size={11} /> {P("Cluster: Export")}</div>
          <div data-rid="rep-cluster-import" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><Download size={11} /> {P("Cluster: Import")}</div>
          <div data-rid="rep-download" className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-[10px] dark:border-white/15"><HardDriveDownload size={11} /> {P("Download all data")}</div>
          <div data-rid="rep-delete" className="inline-flex items-center gap-1 rounded-md border border-[#DA1E28]/40 px-2 py-1 text-[10px] text-[#DA1E28]"><Trash2 size={11} /> {P("Delete customer data")}</div>
        </div>
      </div>
      {scene === "edit" && <EditScene P={P} />}
      {activeRid === "rep-delete-confirm" && <DeleteConfirmDialog confirmRid="rep-delete-confirm" />}
    </div>
  );
}

export default function FamiligiReplicaTour() {
  return (
    <ReplicaTour app="family_tree" Pane={FamiligiPane} target={TARGET}
      prefixSteps={MASTER_ENABLER_STEPS} PrefixPane={MasterEnablerPane}
      prefixLabel={MASTER_ENABLER_SECTION} appLabel="Familigi™ — Family Tree"
      meTargets={["rep-cluster-export", "rep-cluster-import", "rep-download", "rep-delete", "rep-delete-confirm"]}
      note="Faithful replica of the Familigi™ screen — Family (cards), Generic (org-chart), diagram and relationship-linking scenes. The hand follows the verbatim manual and lands on each real control. (English narration; localization to follow.)" />
  );
}
